import React from 'react';
import {
  TRIP_IMPORT_ENTRY_SUBTITLE,
  TRIP_IMPORT_ENTRY_TITLE,
} from '@/constants/tripImportEntryCopy';
import {
  DocIcon,
  LinkIcon,
  ListIcon,
  PinIcon,
  SocialStackIcon,
  TripIcon,
} from './MobileCreateMenuIcons';

export type MobileCreateMenuVariant = 'creator' | 'traveller';

export interface MobileCreateMenuHandlers {
  onCreateNewLink: () => void;
  onMyLinks: () => void;
  onImportSocial: () => void;
  onRecommend: () => void;
  onPasteText: () => void;
  onCreateList: () => void;
  onCreateTrip: () => void;
}

interface MobileCreateMenuSheetProps {
  variant: MobileCreateMenuVariant;
  closing: boolean;
  onAnimationEnd: () => void;
  handlers: MobileCreateMenuHandlers;
}

interface TileProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  ariaLabel?: string;
}

const Tile: React.FC<TileProps> = ({ icon, title, subtitle, onClick, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel ?? title}
    className="flex w-full flex-col items-start gap-2 rounded-button-md bg-white p-4 text-left shadow-surface transition-colors active:bg-gray-100"
  >
    <span className="inline-flex h-6 items-center">{icon}</span>
    <span className="flex flex-col">
      <span className="font-manrope text-[15px] font-semibold leading-[20px] text-gray-900">
        {title}
      </span>
      <span className="font-manrope text-[13px] leading-[18px] text-gray-500">
        {subtitle}
      </span>
    </span>
  </button>
);

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div className="mb-3 flex flex-col gap-[2px] px-1">
    <span className="font-lora text-[18px] font-semibold leading-[24px] text-gray-900">
      {title}
    </span>
    {subtitle ? (
      <span className="font-manrope text-[13px] leading-[18px] text-gray-500">{subtitle}</span>
    ) : null}
  </div>
);

/**
 * The create-menu sheet card, portalized by MobileTabBar. Two variants:
 * - creator/advisor: Creator Quick Links + Create Travel Content sections
 * - traveller: single Add Travel Inspo section
 */
const MobileCreateMenuSheet: React.FC<MobileCreateMenuSheetProps> = ({
  variant,
  closing,
  onAnimationEnd,
  handlers,
}) => {
  const className = `mobile-tab-menu mobile-tab-menu--sheet${closing ? ' mobile-tab-menu--closing' : ''}`;

  return (
    <div className={className} role="menu" onAnimationEnd={onAnimationEnd}>
      <div className="flex w-full flex-col gap-5 rounded-card bg-white p-4 shadow-card">
        {variant === 'creator' ? (
          <>
            <section aria-labelledby="mobile-create-creator-quick">
              <SectionHeader title="Creator Quick Links" />
              <div className="flex flex-col gap-2">
                <Tile
                  icon={<PinIcon />}
                  title="Create New Link"
                  subtitle="Share a place & earn when someone books."
                  onClick={handlers.onCreateNewLink}
                />
                <Tile
                  icon={<LinkIcon />}
                  title="My links"
                  subtitle="View & share your existing links."
                  onClick={handlers.onMyLinks}
                />
              </div>
            </section>

            <section aria-labelledby="mobile-create-travel-content">
              <SectionHeader title="Create Travel Content" />
              <div className="flex flex-col gap-2">
                <Tile
                  icon={<SocialStackIcon />}
                  title="Import from social media"
                  subtitle="Save inspo & AI organizes places into lists"
                  onClick={handlers.onImportSocial}
                />
                <Tile
                  icon={<DocIcon />}
                  title={TRIP_IMPORT_ENTRY_TITLE}
                  subtitle={TRIP_IMPORT_ENTRY_SUBTITLE}
                  onClick={handlers.onPasteText}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Tile
                    icon={<ListIcon />}
                    title="Create list"
                    subtitle="Share places"
                    onClick={handlers.onCreateList}
                  />
                  <Tile
                    icon={<TripIcon />}
                    title="Create trip"
                    subtitle="Plan an itinerary"
                    onClick={handlers.onCreateTrip}
                  />
                </div>
              </div>
            </section>
          </>
        ) : (
          <section aria-labelledby="mobile-create-travel-inspo">
            <SectionHeader title="Add Travel Inspo" subtitle="or start planning" />
            <div className="flex flex-col gap-2">
              <Tile
                icon={<SocialStackIcon />}
                title="Import from social media"
                subtitle="Save inspo & AI organizes places into lists"
                onClick={handlers.onImportSocial}
              />
              <div className="grid grid-cols-2 gap-2">
                <Tile
                  icon={<PinIcon />}
                  title="Recommend"
                  subtitle="Share one place"
                  onClick={handlers.onRecommend}
                />
                <Tile
                  icon={<DocIcon />}
                  title={TRIP_IMPORT_ENTRY_TITLE}
                  subtitle={TRIP_IMPORT_ENTRY_SUBTITLE}
                  onClick={handlers.onPasteText}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Tile
                  icon={<ListIcon />}
                  title="Create list"
                  subtitle="Save places"
                  onClick={handlers.onCreateList}
                />
                <Tile
                  icon={<TripIcon />}
                  title="Create trip"
                  subtitle="Build an itinerary"
                  onClick={handlers.onCreateTrip}
                />
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default MobileCreateMenuSheet;
